export const defaultColumnConfig = {
  width: "160px",
};

export const defaultTableConfig = {
  headerBackground: "#f3f4f6",
  headerTextColor: "#111827",
  bodyColor: "#ffffff",
  bodyTextColor: "#111827",
  alternateRowColor: "#f9fafb",
  borderColor: "#d1d5db",
  borderWidth: 1,
  borderStyle: "solid",
  cellPadding: 8,
  borderRadius: 6,
  textAlign: "left",
  fontFamily: "Arial",
  fontSize: 14,
  headerBold: false,
  uppercaseHeader: false,
  headerBorder: false,
  headerTextOverride: {},
  stripedRows: false,
  columnDividers: true,
  rowBorders: true,
  outerBorder: true,
};

const TABLE_CONFIG_STYLE_KEYS = {
  headerBackground: "--tb-header-bg",
  headerTextColor: "--tb-header-text",
  bodyColor: "--tb-body-bg",
  bodyTextColor: "--tb-body-text",
  alternateRowColor: "--tb-alt-row-bg",
  borderColor: "--tb-border-color",
  borderWidth: "--tb-border-width",
  borderStyle: "--tb-border-style",
  cellPadding: "--tb-cell-padding",
  borderRadius: "--tb-border-radius",
  textAlign: "--tb-text-align",
  fontFamily: "--tb-font-family",
  fontSize: "--tb-font-size",
  headerBold: "--tb-header-bold",
  uppercaseHeader: "--tb-header-uppercase",
  headerBorder: "--tb-header-border",
  headerTextOverride: "--tb-header-text-override",
  stripedRows: "--tb-striped-rows",
  columnDividers: "--tb-column-dividers",
  rowBorders: "--tb-row-borders",
  outerBorder: "--tb-outer-border",
};

const TABLE_COLUMN_WIDTHS_STYLE_KEY = "--tb-col-widths";
const TABLE_COLUMN_ALIGNMENTS_STYLE_KEY = "--tb-col-alignments";

const toBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    return value === "true";
  }
  return fallback;
};

const toNumber = (value, fallback = 0) => {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
};

export const normalizePixelValue = (value, fallback = "160px") => {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return `${value}px`;
  }
  if (typeof value === "string" && value.trim()) {
    return value.endsWith("px") ? value : `${parseInt(value, 10) || 0}px`;
  }
  return fallback;
};

export const normalizeTableColumn = (column = {}) => ({
  ...column,
  width: normalizePixelValue(column.width, defaultColumnConfig.width),
});

export const normalizeTableConfig = (tableConfig = {}) => {
  const merged = {
    ...defaultTableConfig,
    ...(tableConfig || {}),
  };

  return {
    ...merged,
    borderWidth: toNumber(merged.borderWidth, defaultTableConfig.borderWidth),
    cellPadding: toNumber(merged.cellPadding, defaultTableConfig.cellPadding),
    borderRadius: toNumber(merged.borderRadius, defaultTableConfig.borderRadius),
    fontSize: toNumber(merged.fontSize, defaultTableConfig.fontSize),
    headerBold: toBoolean(merged.headerBold, defaultTableConfig.headerBold),
    uppercaseHeader: toBoolean(
      merged.uppercaseHeader,
      defaultTableConfig.uppercaseHeader,
    ),
    headerBorder: toBoolean(merged.headerBorder, defaultTableConfig.headerBorder),
    stripedRows: toBoolean(merged.stripedRows, defaultTableConfig.stripedRows),
    columnDividers: toBoolean(
      merged.columnDividers,
      defaultTableConfig.columnDividers,
    ),
    rowBorders: toBoolean(merged.rowBorders, defaultTableConfig.rowBorders),
    outerBorder: toBoolean(merged.outerBorder, defaultTableConfig.outerBorder),
    headerTextOverride:
      merged.headerTextOverride && typeof merged.headerTextOverride === "object"
        ? merged.headerTextOverride
        : {},
  };
};

const deserializeTableConfigFromStyles = (styles = {}) => ({
  headerBackground:
    styles[TABLE_CONFIG_STYLE_KEYS.headerBackground] || defaultTableConfig.headerBackground,
  headerTextColor:
    styles[TABLE_CONFIG_STYLE_KEYS.headerTextColor] || defaultTableConfig.headerTextColor,
  bodyColor: styles[TABLE_CONFIG_STYLE_KEYS.bodyColor] || defaultTableConfig.bodyColor,
  bodyTextColor:
    styles[TABLE_CONFIG_STYLE_KEYS.bodyTextColor] || defaultTableConfig.bodyTextColor,
  alternateRowColor:
    styles[TABLE_CONFIG_STYLE_KEYS.alternateRowColor] || defaultTableConfig.alternateRowColor,
  borderColor: styles[TABLE_CONFIG_STYLE_KEYS.borderColor] || defaultTableConfig.borderColor,
  borderWidth: toNumber(
    styles[TABLE_CONFIG_STYLE_KEYS.borderWidth],
    defaultTableConfig.borderWidth,
  ),
  borderStyle: styles[TABLE_CONFIG_STYLE_KEYS.borderStyle] || defaultTableConfig.borderStyle,
  cellPadding: toNumber(
    styles[TABLE_CONFIG_STYLE_KEYS.cellPadding],
    defaultTableConfig.cellPadding,
  ),
  borderRadius: toNumber(
    styles[TABLE_CONFIG_STYLE_KEYS.borderRadius],
    defaultTableConfig.borderRadius,
  ),
  textAlign: styles[TABLE_CONFIG_STYLE_KEYS.textAlign] || defaultTableConfig.textAlign,
  fontFamily:
    styles[TABLE_CONFIG_STYLE_KEYS.fontFamily] || defaultTableConfig.fontFamily,
  fontSize: toNumber(
    styles[TABLE_CONFIG_STYLE_KEYS.fontSize],
    defaultTableConfig.fontSize,
  ),
  headerBold: toBoolean(
    styles[TABLE_CONFIG_STYLE_KEYS.headerBold],
    defaultTableConfig.headerBold,
  ),
  uppercaseHeader: toBoolean(
    styles[TABLE_CONFIG_STYLE_KEYS.uppercaseHeader],
    defaultTableConfig.uppercaseHeader,
  ),
  headerBorder: toBoolean(
    styles[TABLE_CONFIG_STYLE_KEYS.headerBorder],
    defaultTableConfig.headerBorder,
  ),
  headerTextOverride: (() => {
    const raw = styles[TABLE_CONFIG_STYLE_KEYS.headerTextOverride];
    if (!raw || typeof raw !== "string") {
      return defaultTableConfig.headerTextOverride;
    }
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch (error) {
      return {};
    }
  })(),
  stripedRows: toBoolean(
    styles[TABLE_CONFIG_STYLE_KEYS.stripedRows],
    defaultTableConfig.stripedRows,
  ),
  columnDividers: toBoolean(
    styles[TABLE_CONFIG_STYLE_KEYS.columnDividers],
    defaultTableConfig.columnDividers,
  ),
  rowBorders: toBoolean(
    styles[TABLE_CONFIG_STYLE_KEYS.rowBorders],
    defaultTableConfig.rowBorders,
  ),
  outerBorder: toBoolean(
    styles[TABLE_CONFIG_STYLE_KEYS.outerBorder],
    defaultTableConfig.outerBorder,
  ),
});

const serializeTableConfigToStyles = (styles = {}, tableConfig = {}) => {
  const resolvedConfig = normalizeTableConfig(tableConfig);
  return {
    ...styles,
    [TABLE_CONFIG_STYLE_KEYS.headerBackground]: resolvedConfig.headerBackground,
    [TABLE_CONFIG_STYLE_KEYS.headerTextColor]: resolvedConfig.headerTextColor,
    [TABLE_CONFIG_STYLE_KEYS.bodyColor]: resolvedConfig.bodyColor,
    [TABLE_CONFIG_STYLE_KEYS.bodyTextColor]: resolvedConfig.bodyTextColor,
    [TABLE_CONFIG_STYLE_KEYS.alternateRowColor]: resolvedConfig.alternateRowColor,
    [TABLE_CONFIG_STYLE_KEYS.borderColor]: resolvedConfig.borderColor,
    [TABLE_CONFIG_STYLE_KEYS.borderWidth]: String(resolvedConfig.borderWidth),
    [TABLE_CONFIG_STYLE_KEYS.borderStyle]: resolvedConfig.borderStyle,
    [TABLE_CONFIG_STYLE_KEYS.cellPadding]: String(resolvedConfig.cellPadding),
    [TABLE_CONFIG_STYLE_KEYS.borderRadius]: String(resolvedConfig.borderRadius),
    [TABLE_CONFIG_STYLE_KEYS.textAlign]: resolvedConfig.textAlign,
    [TABLE_CONFIG_STYLE_KEYS.fontFamily]: resolvedConfig.fontFamily,
    [TABLE_CONFIG_STYLE_KEYS.fontSize]: String(resolvedConfig.fontSize),
    [TABLE_CONFIG_STYLE_KEYS.headerBold]: String(!!resolvedConfig.headerBold),
    [TABLE_CONFIG_STYLE_KEYS.uppercaseHeader]: String(!!resolvedConfig.uppercaseHeader),
    [TABLE_CONFIG_STYLE_KEYS.headerBorder]: String(!!resolvedConfig.headerBorder),
    [TABLE_CONFIG_STYLE_KEYS.headerTextOverride]: JSON.stringify(
      resolvedConfig.headerTextOverride || {},
    ),
    [TABLE_CONFIG_STYLE_KEYS.stripedRows]: String(!!resolvedConfig.stripedRows),
    [TABLE_CONFIG_STYLE_KEYS.columnDividers]: String(!!resolvedConfig.columnDividers),
    [TABLE_CONFIG_STYLE_KEYS.rowBorders]: String(!!resolvedConfig.rowBorders),
    [TABLE_CONFIG_STYLE_KEYS.outerBorder]: String(!!resolvedConfig.outerBorder),
  };
};

const deserializeColumnWidthsFromStyles = (styles = {}) => {
  const raw = styles[TABLE_COLUMN_WIDTHS_STYLE_KEY];
  if (!raw || typeof raw !== "string") {
    return [];
  }
  return raw
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean)
    .map((value) => normalizePixelValue(value, defaultColumnConfig.width));
};

const serializeColumnWidthsToStyles = (styles = {}, columns = []) => {
  const widths = columns.map((column) =>
    normalizePixelValue(column?.width, defaultColumnConfig.width),
  );
  return {
    ...styles,
    [TABLE_COLUMN_WIDTHS_STYLE_KEY]: widths.join(","),
  };
};

const deserializeColumnAlignmentsFromStyles = (styles = {}) => {
  const raw = styles[TABLE_COLUMN_ALIGNMENTS_STYLE_KEY];
  if (!raw || typeof raw !== "string") {
    return [];
  }
  return raw
    .split(",")
    .map((value) => value.trim())
    .map((value) => (value === "left" || value === "center" || value === "right" ? value : ""));
};

const serializeColumnAlignmentsToStyles = (styles = {}, columns = []) => {
  const alignments = columns.map((column) => column?.textAlign || "");
  return {
    ...styles,
    [TABLE_COLUMN_ALIGNMENTS_STYLE_KEY]: alignments.join(","),
  };
};

export const normalizeTableElement = (element = {}) => {
  if (element.fieldPlaceHolder !== "{TABLE}") {
    return element;
  }

  const styles = element.styles || {};
  const styleTableConfig = deserializeTableConfigFromStyles(styles);
  const mergedTableConfig = normalizeTableConfig({
    ...styleTableConfig,
    ...(element.tableConfig || {}),
  });
  const styleColumnWidths = deserializeColumnWidthsFromStyles(styles);
  const styleColumnAlignments = deserializeColumnAlignmentsFromStyles(styles);

  const normalizedColumns = Array.isArray(element.columns)
    ? element.columns.map((column, index) =>
        normalizeTableColumn({
          ...column,
          width: column?.width || styleColumnWidths[index] || defaultColumnConfig.width,
          textAlign: column?.textAlign || styleColumnAlignments[index] || "",
        }),
      )
    : [];

  const stylesWithTableConfig = serializeTableConfigToStyles(styles, mergedTableConfig);
  const finalStyles = serializeColumnWidthsToStyles(
    stylesWithTableConfig,
    normalizedColumns,
  );
  const finalStylesWithAlignments = serializeColumnAlignmentsToStyles(
    finalStyles,
    normalizedColumns,
  );

  return {
    ...element,
    styles: finalStylesWithAlignments,
    tableConfig: mergedTableConfig,
    columns: normalizedColumns,
  };
};

export const hasMissingTableDefaults = (element = {}) => {
  if (element.fieldPlaceHolder !== "{TABLE}") {
    return false;
  }

  if (!element.tableConfig) {
    return true;
  }

  const hasMissingConfigKey = Object.keys(defaultTableConfig).some(
    (key) => typeof element.tableConfig[key] === "undefined",
  );
  if (hasMissingConfigKey) {
    return true;
  }

  const columns = Array.isArray(element.columns) ? element.columns : [];
  return columns.some((column) => !column || !column.width);
};
